// ============================================
// PAGE - Ventas (Punto de Venta / POS)
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchProductosVendibles, useSearchPacksVendibles, useCountProductosVendibles, useProductosVendiblesBarcode, usePacksVendiblesBarcode, productoRepository, useCartStore, useUIStore, useObtenerPack, useUpdateProductoAlmacen, useAgregarProductoAlmacen, useMiTurno, getFechaHoyLocal, type ProductoVendible, usePedidosPendientesCount, useAuthStore } from '@almacenes/core';
import { CalculatorModal } from '@/components/CalculatorModal';
import { ModalCheckout } from './components/ModalCheckout';
import { BuscarImagenModal } from '@/components/BuscarImagenModal';
import { ConfirmacionModal } from '@/components/ConfirmacionModal';
import { BarcodeScannerModal } from '@/components/BarcodeScannerModal';
import { useBarcodeScan } from '@/hooks/useBarcodeScan';
import { usePrintBoleta } from '@/hooks/usePrintBoleta';
import { descuentoPorcentualEnPesos, esMontoDescuentoValido } from '@/utils/descuento';
import { GestionCajaModal } from '../caja/components/GestionCajaModal';
import {
  MdSearch,
  MdDelete,
  MdAdd,
  MdRemove,
  MdPrint,
  MdShoppingCart,
  MdRefresh,
  MdImage,
  MdPointOfSale,
  MdClose,
  MdReceipt,
  MdViewModule,
  MdViewList,
  MdGridView,
  MdApps,
} from 'react-icons/md';
import { FaBarcode, FaCalculator } from 'react-icons/fa6';
import { CiBarcode } from "react-icons/ci";
import productosPlaceholder from '@/assets/images/imagen_no_disponible.png';

export const VentasPage = () => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedProductForQuantity, setSelectedProductForQuantity] = useState<ProductoVendible | null>(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [granelInputMode, setGranelInputMode] = useState<'kg' | 'monto'>('kg');
  const [showPackModal, setShowPackModal] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null);
  const [selectedPackProducto, setSelectedPackProducto] = useState<ProductoVendible | null>(null);
  const [tabProductos, setTabProductos] = useState<'todos' | 'packs'>('todos');
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedProductForDiscount, setSelectedProductForDiscount] = useState<{productoAlmacenId: number, nombre: string, precio: number, cantidad: number} | null>(null);
  const [discountInput, setDiscountInput] = useState('');
  const [discountType, setDiscountType] = useState<'porcentaje' | 'monto'>('porcentaje');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [mostrarModalImagen, setMostrarModalImagen] = useState(false);
  const [productoSeleccionadoParaImagen, setProductoSeleccionadoParaImagen] = useState<ProductoVendible | null>(null);
  const [scanDetected, setScanDetected] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [productoGlobalEncontrado, setProductoGlobalEncontrado] = useState<any>(null);
  const [precioVentaInput, setPrecioVentaInput] = useState('');
  const [showAvisoCajaModal, setShowAvisoCajaModal] = useState(false);
  const [showGestionCajaModal, setShowGestionCajaModal] = useState(false);
  const [shouldCheckCaja, setShouldCheckCaja] = useState(false);
  const [showConfirmClearCart, setShowConfirmClearCart] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [ultimaBoletaId, setUltimaBoletaId] = useState<number | null>(null);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    // Recuperar preferencia de localStorage (persiste entre sesiones)
    const saved = localStorage.getItem('productViewMode');
    return (saved === 'grid' || saved === 'list') ? saved : 'grid';
  });
  const [gridSize, setGridSize] = useState<'compact' | 'normal'>(() => {
    const saved = localStorage.getItem('productGridSize');
    return saved === 'compact' ? 'compact' : 'normal';
  });
  const [lastAddedProduct, setLastAddedProduct] = useState<{producto: ProductoVendible, cantidad: number, subtotal: number} | null>(null);
  const [showLastAdded, setShowLastAdded] = useState(false);
  const [lastInteractedId, setLastInteractedId] = useState<number | null>(null);
  const [bannerPedidosDismissed, setBannerPedidosDismissed] = useState(false);
  const [editingQuantity, setEditingQuantity] = useState<Record<number, string>>({});
  const pageSize = 20;

  const navigate = useNavigate();
  const session = useAuthStore((state) => state.session);
  const esPropietario = session?.user?.esPropietario ?? false;
  const { data: pedidosPendientes } = usePedidosPendientesCount();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const addToast = useUIStore((state) => state.addToast);
  const updateProductoMutation = useUpdateProductoAlmacen();
  const agregarProductoMutation = useAgregarProductoAlmacen();
  const { printBoleta, loadingPrintId } = usePrintBoleta();

  // Turno de caja del vendedor (módulo nuevo, Caja 360)
  // Solo se necesita saber si hay turno abierto (aviso diario): sin polling, para no consultar cada 30 s por terminal
  const { data: miTurno, isLoading: isLoadingTurno } = useMiTurno();
  const turno = miTurno?.turno ?? null;

  // Cart store
  const cartItems = useCartStore((state) => state.items);
  const addItem = useCartStore((state) => state.addItem);
  const addPackItem = useCartStore((state) => state.addPackItem);
  const addCalculatorItem = useCartStore((state) => state.addCalculatorItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const setItemDiscount = useCartStore((state) => state.setItemDiscount);
  const removeItem = useCartStore((state) => state.removeItem);
  const clearCart = useCartStore((state) => state.clearCart);
  const selectItem = useCartStore((state) => state.selectItem);
  const selectedItemIndex = useCartStore((state) => state.selectedItemIndex);
  const getTotal = useCartStore((state) => state.getTotal);
  const getItemCount = useCartStore((state) => state.getItemCount);

  // Guardar preferencias de vista cuando cambien
  useEffect(() => {
    localStorage.setItem('productViewMode', viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem('productGridSize', gridSize);
  }, [gridSize]);

  // Mostrar el banner de nuevo si llegan más pedidos pendientes
  useEffect(() => {
    if ((pedidosPendientes ?? 0) > 0) {
      setBannerPedidosDismissed(false);
    }
  }, [pedidosPendientes]);

  // Fetch products (buscador/escáner del POS, Anterior/Siguiente sin COUNT(*) en el backend)
  // Cada pestaña consulta solo cuando está activa: "Mis Productos" no genera requests a /packs.
  const esTabPacks = tabProductos === 'packs';
  const productosQuery = useSearchProductosVendibles(currentPage, searchTerm, pageSize, undefined, !esTabPacks, true);
  const packsQuery = useSearchPacksVendibles(currentPage, searchTerm, pageSize, esTabPacks);
  const { data, isLoading, error } = esTabPacks ? packsQuery : productosQuery;
  const { data: countData } = useCountProductosVendibles();

  const content = data?.content ?? [];
  const hasNextPage = data?.hasNext ?? false;

  const fetchProductosPorBarcode = useProductosVendiblesBarcode();
  const fetchPacksPorBarcode = usePacksVendiblesBarcode();

  // Fetch pack details when a pack is selected
  const { data: packData, isLoading: isLoadingPack } = useObtenerPack(
    selectedPackId || 0
  );

  // Focus en input de cantidad cuando se abre el modal
  useEffect(() => {
    if (showQuantityModal) {
      setTimeout(() => {
        quantityInputRef.current?.focus();
      }, 100);
    }
  }, [showQuantityModal]);

  // Auto-foco en búsqueda al cargar
  useEffect(() => { searchInputRef.current?.focus(); }, []);

  // Atajos de teclado: / o Ctrl+K para foco, Esc para limpiar búsqueda
  useEffect(() => {
    const noModalAbierto = !showCalculator && !showQuantityModal && !showPackModal &&
      !showDiscountModal && !showCheckoutModal && !mostrarModalImagen &&
      !showAddProductModal && !showAvisoCajaModal && !showGestionCajaModal && !showBarcodeScanner;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!noModalAbierto) return;
      if (e.key === 'Escape') { setSearchInput(''); searchInputRef.current?.focus(); return; }
      if (e.key === '/' || (e.ctrlKey && e.key === 'k')) {
        if (document.activeElement !== searchInputRef.current) { e.preventDefault(); searchInputRef.current?.focus(); }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCalculator, showQuantityModal, showPackModal, showDiscountModal, showCheckoutModal,
      mostrarModalImagen, showAddProductModal, showAvisoCajaModal, showGestionCajaModal, showBarcodeScanner]);


  // Verificar status de caja una vez al día
  useEffect(() => {
    const checkCajaStatus = () => {
      const lastCheck = localStorage.getItem('lastCajaCheck');
      const today = getFechaHoyLocal();
      
      // Si no hay registro o es un día diferente, verificar
      if (!lastCheck || lastCheck !== today) {
        setShouldCheckCaja(true);
        localStorage.setItem('lastCajaCheck', today);
      }
    };

    checkCajaStatus();
  }, []);

  // Búsqueda automática con debounce
  useEffect(() => {
    // Solo buscar si tiene 3+ caracteres o está vacío (2 chars van por exact match vía Enter/scanner)
    if (searchInput.length === 0 || searchInput.length >= 3) {
      const timer = setTimeout(() => {
        setSearchTerm(searchInput);
        setCurrentPage(0);
      }, 500); // Esperar 500ms después de que el usuario deje de escribir

      return () => clearTimeout(timer);
    }
  }, [searchInput]);

  // Manejar respuesta del turno de caja: si no hay uno abierto hoy, avisar (Caja 360)
  useEffect(() => {
    if (!shouldCheckCaja || isLoadingTurno) return;
    if (!turno) {
      setShowAvisoCajaModal(true);
    }
    // Desactivar futuras verificaciones automáticas
    setShouldCheckCaja(false);
  }, [shouldCheckCaja, turno, isLoadingTurno]);

  // En la pestaña Packs un número también equivale al código con prefijo ("12345" = "P-12345"):
  // los packs se guardan como "P-<código>" y el cajero o la pistola pueden entregar solo el número.
  const coincideCodigo = (codigo: string | undefined, termino: string) => {
    const c = codigo?.toLowerCase();
    const t = termino.toLowerCase();
    return c === t || (esTabPacks && /^\d+$/.test(t) && c === `p-${t}`);
  };

  const findAndAddForBarcode = async (barcode: string) => {
    // Pestaña Packs: el código se busca solo entre packs; no hay balanza ni catálogo global
    if (esTabPacks) {
      const resultPacks = await fetchPacksPorBarcode(barcode);
      const pack = resultPacks?.content.find((p) => coincideCodigo(p.codigo, barcode));
      if (pack) {
        handleAddToCart(pack);
      } else {
        addToast('error', `No se encontró ningún pack con el código "${barcode}"`);
      }
      return;
    }

    const esCodigoBarrasEstandar = /^\d{8,}$/.test(barcode);
    // Códigos de 2 dígitos (granel): traer más resultados ordenados por código
    // para que "10" aparezca antes que "100", "1000", etc.
    const result = barcode.length <= 2
      ? await fetchProductosPorBarcode(barcode, 500, 'codigo,asc')
      : await fetchProductosPorBarcode(barcode);

    let exactMatch = result?.content.find(
      (p) => p.codigo?.toLowerCase() === barcode.toLowerCase()
    );

    // Código de balanza (13 dígitos con prefijo 21 = precio, 22 = peso): el backend resuelve el producto
    // y calcula precioBalanza/stockBalanza. El código guardado es el 20 + PLU (dígitos 3-7) + 00000 + verificador.
    if (!exactMatch && /^2[12]\d{11}$/.test(barcode)) {
      const plu = barcode.slice(2, 7);
      exactMatch = result?.content.find(
        (p) => p.venderPor === 'F' && /^20\d{11}$/.test(p.codigo ?? '') && p.codigo.slice(2, 7) === plu
      );
    }

    if (exactMatch) {
      // Match exacto encontrado, agregar al carrito automáticamente
      handleAddToCart(exactMatch);
    } else {
      // Solo consultar catálogo global para códigos de barras estándar (EAN-8 mínimo)
      if (!esCodigoBarrasEstandar) {
        addToast('error', `No se encontró ningún producto con el código "${barcode}"`);
        return;
      }

      const productoGlobal = await productoRepository.scanBarcode(barcode);

      if(!productoGlobal || !productoGlobal.id) {
         addToast('error', `No se encontró ningún producto con el código "${barcode}"`);
         return;
      }

      // Producto encontrado en base de datos global, abrir modal para agregar al inventario
      setProductoGlobalEncontrado(productoGlobal);
      setPrecioVentaInput('');
      setShowAddProductModal(true);
    }
  }

  // Hook para detectar escaneos de código de barras de forma global
  useBarcodeScan({
    onScan: async (barcode) => {      
      // Limpiar input de búsqueda inmediatamente para indicar que el hook tomó control
      setSearchInput('');
      setSearchTerm('');
      
      // Mostrar indicador de escaneo
      setScanDetected(true);
      setTimeout(() => setScanDetected(false), 2000);

      await findAndAddForBarcode(barcode);
    },
    // Deshabilitar cuando hay modales abiertos para evitar interferencias
    enabled: !showCalculator && !showQuantityModal && !showPackModal &&
             !showDiscountModal && !showCheckoutModal && !mostrarModalImagen &&
             !showAddProductModal && !showAvisoCajaModal && !showGestionCajaModal &&
             !showBarcodeScanner,
    minLength: 2,
    // Solo la pestaña Packs entiende códigos "P-..."; Mis Productos sigue aceptando solo dígitos
    permitirPrefijoPack: esTabPacks,
  });

  // Manejo de tecla Enter (para match exacto con escáner)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = searchInput.trim();

      if (query.length === 2) {
        // Código corto (ej. granel "10"): búsqueda exacta por código vía API
        findAndAddForBarcode(query);
      } else if (query.length >= 3) {
        // 3+ chars: verificar match exacto en resultados actuales
        setTimeout(() => {
          checkExactMatch(query);
        }, 100);
      }
    }
  }

  // Verificar match exacto por código de barras o SKU (para búsqueda manual)
  const checkExactMatch = (query: string) => {
    if (content.length === 0) return;

    const exactMatch = content.find((p) => coincideCodigo(p.codigo, query));

    if (exactMatch) {
      // Match exacto encontrado, agregar al carrito automáticamente
      handleAddToCart(exactMatch);
      setSearchInput('');
      setSearchTerm('');
      searchInputRef.current?.focus();
    }
  };

  // Lee cantidad y subtotal acumulados del ítem en el carrito (ya actualizado por Zustand)
  const getCartItemState = (productoAlmacenId: number): { cantidad: number; subtotal: number } => {
    const item = useCartStore.getState().items.find(i => i.producto.productoAlmacenId === productoAlmacenId);
    return { cantidad: item?.cantidad ?? 1, subtotal: item?.subtotal ?? 0 };
  };

  // Agregar producto al carrito
  const handleAddToCart = (producto: ProductoVendible) => {
    if (!producto.precioVentaActual) {
      addToast('error', 'Este producto no tiene precio de venta configurado');
      return;
    }

    /*if (producto.stock <= 0) {
      addToast('warning', 'Este producto no tiene stock disponible');
    }*/

    // Si es un pack, abrir modal para mostrar detalles
    if (producto.esPack) {
      setSelectedPackId(producto.productoAlmacenId);
      setSelectedPackProducto(producto);
      setShowPackModal(true);
      return;
    }

    // Fracción con precio y cantidad incorporados en el código (balanza): agregar directo sin modal
    if (
      producto.venderPor === 'F' &&
      producto.precioBalanza != null &&
      producto.stockBalanza != null
    ) {
      addItem(producto, producto.stockBalanza, Math.round(producto.precioBalanza));

      setLastAddedProduct({ producto, ...getCartItemState(producto.productoAlmacenId) });
      setShowLastAdded(true);
      setLastInteractedId(producto.productoAlmacenId);
      return;
    }

    // Si el producto se vende por fracción, pedir cantidad
    if (producto.venderPor === 'F') {
      setSelectedProductForQuantity(producto);
      setQuantityInput('');
      setGranelInputMode('kg');
      setShowQuantityModal(true);
      return;
    }

    // Si es por unidad, agregar directamente
    addItem(producto, 1);

    // Mostrar visualización grande para el cliente
    setLastAddedProduct({ producto, ...getCartItemState(producto.productoAlmacenId) });
    setShowLastAdded(true);
    setLastInteractedId(producto.productoAlmacenId);
  };

  // Confirmar cantidad para producto fraccionado
  const handleConfirmQuantity = () => {
    if (!selectedProductForQuantity) return;

    let cantidad: number;
    let subtotalExacto: number | undefined;

    if (granelInputMode === 'monto') {
      const valorIngresado = parseFloat(quantityInput);

      if (isNaN(valorIngresado) || valorIngresado <= 0) {
        addToast('error', 'Ingresa un monto válido');
        return;
      }

      const precio = selectedProductForQuantity.precioVentaActual || 0;
      if (precio <= 0) {
        addToast('error', 'El producto no tiene precio definido');
        return;
      }

      cantidad = Math.round((valorIngresado / precio) * 100000) / 100000;
      if (cantidad < 0.00001) {
        addToast('error', 'El monto ingresado es insuficiente para el precio por kg');
        return;
      }
      subtotalExacto = Math.round(valorIngresado);
    } else {
      const normalizedInput = quantityInput.replace(',', '.');
      const valorIngresado = parseFloat(normalizedInput);

      if (isNaN(valorIngresado) || valorIngresado <= 0) {
        addToast('error', 'Ingresa una cantidad válida');
        return;
      }
      cantidad = valorIngresado;
    }

    addItem(selectedProductForQuantity, cantidad, subtotalExacto);

    // Mostrar visualización grande para el cliente
    setLastAddedProduct({ producto: selectedProductForQuantity, ...getCartItemState(selectedProductForQuantity.productoAlmacenId) });
    setShowLastAdded(true);
    setLastInteractedId(selectedProductForQuantity.productoAlmacenId);
    setShowQuantityModal(false);
    setSelectedProductForQuantity(null);
    setQuantityInput('');
  };

  // Confirmar pack y agregar al carrito
  const handleConfirmPack = () => {
    if (!packData || !selectedPackId) return;

    // Se guarda al abrir el modal: el pack puede no estar en la página visible (p. ej. llegó por escáner)
    const producto = selectedPackProducto;
    if (!producto) return;

    // Agregar pack al carrito con sus detalles
    addPackItem(producto, packData);

    // Mostrar visualización grande para el cliente
    setLastAddedProduct({ producto, ...getCartItemState(producto.productoAlmacenId) });
    setShowLastAdded(true);
    setLastInteractedId(producto.productoAlmacenId);
    setShowPackModal(false);
    setSelectedPackId(null);
    setSelectedPackProducto(null);
  };

  // Cambiar de pestaña (Mis Productos | Packs): vuelve a la primera página; la búsqueda se mantiene
  const handleCambiarTab = (tab: 'todos' | 'packs') => {
    if (tab === tabProductos) return;
    setTabProductos(tab);
    setCurrentPage(0);
  };

  // Incrementar cantidad
  const handleIncrement = (productoAlmacenId: number, currentQuantity: number, venderPor: 'U' | 'F', isPack: boolean = false) => {
    // Para packs siempre incrementar de 1 en 1
    const increment = isPack || venderPor === 'U' ? 1 : 0.1;
    updateQuantity(productoAlmacenId, currentQuantity + increment);
  };

  // Decrementar cantidad
  const handleDecrement = (productoAlmacenId: number, currentQuantity: number, venderPor: 'U' | 'F', isPack: boolean = false) => {
    // Para packs siempre decrementar de 1 en 1
    const decrement = isPack || venderPor === 'U' ? 1 : 0.1;
    const newQuantity = currentQuantity - decrement;

    if (newQuantity <= 0) {
      removeItem(productoAlmacenId);
    } else {
      updateQuantity(productoAlmacenId, newQuantity);
    }
  };

  // Si un ítem sale del carrito por cualquier vía (botón eliminar, vaciar carrito,
  // venta finalizada desde el modal de checkout, etc.), se descarta su texto de
  // edición pendiente para que no lo herede si el mismo producto se vuelve a agregar
  useEffect(() => {
    setEditingQuantity((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const activeIds = new Set(cartItems.map((i) => i.producto.productoAlmacenId));
      const next: Record<number, string> = {};
      let changed = false;
      for (const key of Object.keys(prev)) {
        const id = Number(key);
        if (activeIds.has(id)) {
          next[id] = prev[id];
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [cartItems]);

  // Cambio manual de cantidad. Mientras el usuario escribe (ej. borra "1" para
  // poner "200"), el input queda en blanco momentáneamente: no debe eliminarse
  // el ítem en ese instante, solo cuando termine de editar (ver handleQuantityBlur).
  const handleQuantityChange = (productoAlmacenId: number, value: string, venderPor: 'U' | 'F', isPack: boolean = false) => {
    setEditingQuantity((prev) => ({ ...prev, [productoAlmacenId]: value }));

    // El usuario puede ingresar la coma como separador decimal (formato chileno)
    const normalizedValue = value.replace(',', '.');
    const parsed = parseFloat(normalizedValue);
    if (isNaN(parsed) || parsed <= 0) {
      return;
    }

    // Para packs y productos por unidad, siempre enteros
    const finalValue = isPack || venderPor === 'U' ? Math.round(parsed) : parsed;
    updateQuantity(productoAlmacenId, finalValue);
  };

  // Al perder el foco, si quedó un valor inválido o vacío, recién ahí se elimina el ítem
  const handleQuantityBlur = (productoAlmacenId: number) => {
    const value = editingQuantity[productoAlmacenId];
    if (value !== undefined) {
      const parsed = parseFloat(value.replace(',', '.'));
      if (isNaN(parsed) || parsed <= 0) {
        removeItem(productoAlmacenId);
      }
      setEditingQuantity((prev) => {
        if (!(productoAlmacenId in prev)) return prev;
        const next = { ...prev };
        delete next[productoAlmacenId];
        return next;
      });
    }
  };

  // Aplicar resultado de calculadora
  const handleApplyCalculator = (value: number) => {
    addCalculatorItem(value);
    
    // Para calculadora, crear un producto temporal para mostrar
    const calculatorProduct: ProductoVendible = {
      productoAlmacenId: -1,
      productoNombre: 'Producto calculado',
      codigo: '',
      precioVentaActual: value,
      stock: 0,
      venderPor: 'U',
      esPack: false,
      imagenUrl: null,
    } as ProductoVendible;
    
    setLastAddedProduct({ producto: calculatorProduct, cantidad: 1, subtotal: Math.round(value) });
    setShowLastAdded(true);
  };

  // Abrir modal de descuento
  const handleOpenDiscountModal = (item: any) => {
    setSelectedProductForDiscount({
      productoAlmacenId: item.producto.productoAlmacenId,
      nombre: item.producto.productoNombre,
      precio: item.producto.precioVentaActual || 0,
      cantidad: item.cantidad
    });
    setDiscountInput(item.descuento?.toString() || '');
    setDiscountType('porcentaje');
    setShowDiscountModal(true);
  };

  // Manejar cambio de imagen
  const handleClickImagen = (e: React.MouseEvent, producto: ProductoVendible) => {
    e.stopPropagation();
    setProductoSeleccionadoParaImagen(producto);
    setMostrarModalImagen(true);
  };

  const handleSeleccionarImagen = async (_imageUrl: string, imagenProductoId: number) => {
    if (!productoSeleccionadoParaImagen) return;

    try {
      await updateProductoMutation.mutateAsync({
        productoAlmacenId: productoSeleccionadoParaImagen.productoAlmacenId,
        dto: { imagenProductoId },
      });

      addToast('success', 'Imagen actualizada exitosamente');
      setMostrarModalImagen(false);
      setProductoSeleccionadoParaImagen(null);
    } catch (error) {
      console.error('Error al actualizar imagen:', error);
      addToast('error', 'Error al actualizar la imagen');
    }
  };

  // Confirmar descuento
  const handleConfirmDiscount = () => {
    if (!selectedProductForDiscount) return;

    const valor = parseFloat(discountInput);
    
    if (isNaN(valor) || valor < 0) {
      addToast('error', 'Ingresa un descuento válido');
      return;
    }

    const subtotalSinDescuento = selectedProductForDiscount.precio * selectedProductForDiscount.cantidad;
    
    // Calcular descuento en pesos según el tipo
    let descuentoEnPesos = 0;
    if (discountType === 'porcentaje') {
      if (valor > 100) {
        addToast('error', 'El porcentaje no puede ser mayor a 100%');
        return;
      }
      descuentoEnPesos = descuentoPorcentualEnPesos(subtotalSinDescuento, valor);
    } else {
      if (!esMontoDescuentoValido(valor)) {
        addToast('error', 'El descuento en pesos debe ser un número entero');
        return;
      }
      descuentoEnPesos = valor;
    }

    if (descuentoEnPesos >= subtotalSinDescuento) {
      addToast('error', 'El descuento no puede ser igual o mayor al total del producto');
      return;
    }

    setItemDiscount(selectedProductForDiscount.productoAlmacenId, descuentoEnPesos);
    
    if (descuentoEnPesos > 0) {
      const mensaje = discountType === 'porcentaje' 
        ? `Descuento del ${valor}% ($${descuentoEnPesos.toLocaleString('es-CL')}) aplicado`
        : `Descuento de $${descuentoEnPesos.toLocaleString('es-CL')} aplicado`;
      addToast('success', mensaje);
    } else {
      addToast('info', 'Descuento eliminado');
    }
    
    setShowDiscountModal(false);
    setSelectedProductForDiscount(null);
    setDiscountInput('');
    setDiscountType('porcentaje');
  };

  // Formatear precio de venta con separador de miles
  const handlePrecioVentaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Solo números
    setPrecioVentaInput(value);
  };

  // Confirmar adición de producto global al inventario
  const handleConfirmAddProduct = async () => {
    if (!productoGlobalEncontrado) return;

    const precioVenta = parseInt(precioVentaInput.replace(/\D/g, ''));
    
    if (isNaN(precioVenta) || precioVenta <= 0) {
      addToast('error', 'Ingresa un precio de venta válido');
      return;
    }

    try {
      // Agregar el producto al inventario
      const nuevoProducto = await agregarProductoMutation.mutateAsync({
        productoId: productoGlobalEncontrado.id || null,
        productoCodigo: productoGlobalEncontrado.codigo,
        productoNombre: productoGlobalEncontrado.nombre,
        imagenProductoId: productoGlobalEncontrado.imagenProductoId,
        precioCompraActual: 0,
        precioVentaActual: precioVenta,
        venderPor: 'U',
        stock: 0,
        fechaVencimiento: null,
        esPack: false,
      });
      
      addToast('success', `Producto "${productoGlobalEncontrado.nombre}" agregado al inventario`);
      
      // Cerrar modal y limpiar estado
      setShowAddProductModal(false);
      setProductoGlobalEncontrado(null);
      setPrecioVentaInput('');
      
      // Agregar automáticamente el producto al carrito
      if (nuevoProducto && nuevoProducto.precioVentaActual) {
        addItem(nuevoProducto as ProductoVendible, 1);
        addToast('success', `${nuevoProducto.productoNombre} agregado al carrito`);
        
        // Mostrar visualización grande para el cliente
        setLastAddedProduct({ producto: nuevoProducto as ProductoVendible, ...getCartItemState(nuevoProducto.productoAlmacenId) });
        setShowLastAdded(true);
        setLastInteractedId(nuevoProducto.productoAlmacenId);
      }
    } catch (error) {
      console.error('Error al agregar producto:', error);
      addToast('error', 'Error al agregar el producto al inventario');
    }
  };

  // Gestión de caja: abre/cierra el turno en un modal sobre Ventas, sin navegar fuera
  // (para no perder el carrito ni el estado de la pantalla de venta).
  const handleIrAMiCaja = () => setShowGestionCajaModal(true);

  // Confirmar limpieza de carrito
  const handleConfirmClearCart = () => {
    clearCart();
    addToast('info', 'Carrito vaciado');
    setShowConfirmClearCart(false);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((p) => p + 1);
    }
  };

  const handleSaleCompleted = (boletaId: number, shouldPrint: boolean) => {
    setUltimaBoletaId(boletaId);

    if (shouldPrint) {
      window.setTimeout(() => {
        printBoleta(boletaId);
      }, 0);
    }
  };

  const total = getTotal();
  const itemCount = getItemCount();

  return (
    <div className="absolute inset-0 overflow-hidden flex flex-col pb-16 lg:pb-0">
      {/* Banner de pedidos ecommerce pendientes */}
      {esPropietario && (pedidosPendientes ?? 0) > 0 && !bannerPedidosDismissed && (
        <div className="flex items-center gap-2 bg-orange-50 border-b border-orange-200 px-4 py-2 text-sm text-orange-800 shrink-0">
          <MdReceipt className="shrink-0 text-base" />
          <span className="flex-1">
            Tenés <strong>{pedidosPendientes}</strong> pedido{(pedidosPendientes ?? 0) > 1 ? 's' : ''} pendiente{(pedidosPendientes ?? 0) > 1 ? 's' : ''} de ecommerce
          </span>
          <button
            onClick={() => navigate('/ecommerce?estado=PENDIENTE')}
            className="font-semibold underline hover:text-orange-900 transition-colors"
          >
            Ver pedidos
          </button>
          <button
            onClick={() => setBannerPedidosDismissed(true)}
            className="ml-2 hover:text-orange-900 transition-colors"
            aria-label="Cerrar"
          >
            <MdClose />
          </button>
        </div>
      )}

      <div className="px-4 md:px-6 lg:px-8 pt-4 pb-2 flex-shrink-0">
        {/* Banner de último producto agregado - Visualización para el cliente */}
        {showLastAdded && lastAddedProduct && (
          <div className="mb-2 animate-slideInDown">
            <div className="bg-gradient-to-r from-[#43BFFF] to-[#2196F3] rounded-lg px-3 py-3 shadow-lg border-2 border-[#43BFFF]">
              <div className="flex items-center gap-3">
                {/* Imagen del producto */}
                <div className="w-14 h-14 rounded overflow-hidden bg-white flex-shrink-0 shadow-sm">
                  <img
                    src={lastAddedProduct.producto.imagenUrl || productosPlaceholder}
                    alt={lastAddedProduct.producto.productoNombre}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = productosPlaceholder;
                    }}
                  />
                </div>

                {/* Información del producto - Todo en línea horizontal */}
                <div className="flex-1 flex items-center gap-3 min-w-0">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-white truncate leading-tight">
                      ✓ {lastAddedProduct.producto.productoNombre}
                    </h2>
                  </div>

                  {/* Precio, Cantidad y Total en línea */}
                  <div className="flex items-center gap-2.5 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xl font-bold text-white leading-tight whitespace-nowrap">
                        ${lastAddedProduct.producto.precioVentaActual?.toLocaleString('es-CL') || 'N/A'}
                      </p>
                    </div>

                    <div className="text-white text-lg opacity-70">×</div>

                    <div className="text-center">
                      <p className="text-xl font-bold text-white leading-tight whitespace-nowrap">
                        {lastAddedProduct.cantidad} {lastAddedProduct.producto.venderPor === 'F' ? 'kg' : 'un'}
                      </p>
                    </div>

                    <div className="text-white text-lg opacity-70">=</div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-white leading-tight whitespace-nowrap">
                        ${lastAddedProduct.subtotal.toLocaleString('es-CL')}
                      </p>
                    </div>
                  </div>

                  {/* Botón cerrar */}
                  <button
                    onClick={() => setShowLastAdded(false)}
                    className="p-1.5 rounded bg-white bg-opacity-20 hover:bg-opacity-30 transition-all flex-shrink-0"
                  >
                    <MdClose className="text-xl text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>{/* fin header flex-shrink-0 */}

      {/* Contenido principal: columnas con scroll propio */}
      <div className="flex-1 overflow-hidden min-h-0 px-4 md:px-6 lg:px-8 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full">
          {/* Panel Izquierdo - Productos */}
          <div className="lg:col-span-7 flex flex-col h-full overflow-hidden">
            {/* Barra de búsqueda */}
            <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-200 mb-3 flex-shrink-0">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Buscar por nombre o código (mín. 2 caracteres)..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                    style={{
                      borderColor: searchInput.length > 0 && searchInput.length < 2 ? '#F59E0B' : '#E5E7EB'
                    }}
                  />
                  <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-400" />
                  {searchInput.length > 0 && searchInput.length < 2 && (
                    <p className="absolute left-2 -bottom-5 text-xs text-orange-600">
                      Ingresa al menos 2 caracteres para buscar
                    </p>
                  )}
                </div>

                {/* Botones solo visibles en desktop */}
                <button
                  onClick={() => setShowCalculator(true)}
                  className="hidden lg:flex px-5 py-3 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95 items-center"
                  style={{ backgroundColor: '#43BFFF' }}
                  title="Abrir calculadora"
                >
                  <FaCalculator className="text-xl" />
                </button>

                <button
                  onClick={handleIrAMiCaja}
                  className="hidden lg:flex px-5 py-3 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95 items-center"
                  style={{ backgroundColor: '#10B981' }}
                  title="Gestionar caja"
                >
                  <MdPointOfSale className="text-xl" />
                </button>

                <button
                  onClick={() => ultimaBoletaId && printBoleta(ultimaBoletaId)}
                  disabled={ultimaBoletaId === null || loadingPrintId !== null}
                  className="hidden lg:flex px-5 py-3 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 items-center"
                  style={{ backgroundColor: '#FACC15', color: '#11194F' }}
                  title={ultimaBoletaId ? 'Imprimir ultima venta' : 'Aun no hay una venta reciente para imprimir'}
                >
                  {loadingPrintId === ultimaBoletaId && ultimaBoletaId !== null ? (
                    <MdRefresh className="text-xl animate-spin" />
                  ) : (
                    <MdPrint className="text-xl" />
                  )}
                </button>
              </div>
            </div>{/* fin barra de búsqueda */}

            {/* Lista de Productos */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" role="tablist" aria-label="Tipo de producto">
                  <button
                    role="tab"
                    aria-selected={!esTabPacks}
                    onClick={() => handleCambiarTab('todos')}
                    className={`px-3 py-1.5 rounded text-sm font-bold transition-all ${!esTabPacks ? 'bg-white shadow-sm' : 'text-gray-500 hover:bg-gray-200'}`}
                    style={!esTabPacks ? { color: '#11194F' } : undefined}
                  >
                    Mis Productos
                  </button>
                  <button
                    role="tab"
                    aria-selected={esTabPacks}
                    onClick={() => handleCambiarTab('packs')}
                    className={`px-3 py-1.5 rounded text-sm font-bold transition-all ${esTabPacks ? 'bg-white shadow-sm text-purple-700' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    Packs
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {/* Toggle tamaño tarjetas (solo desktop + modo grid) */}
                  {viewMode === 'grid' && (
                    <div className="hidden lg:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setGridSize('normal')}
                        className={`p-2 rounded transition-all ${gridSize === 'normal' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                        title="Tarjetas grandes"
                      >
                        <MdGridView className={`text-xl ${gridSize === 'normal' ? 'text-blue-600' : 'text-gray-500'}`} />
                      </button>
                      <button
                        onClick={() => setGridSize('compact')}
                        className={`p-2 rounded transition-all ${gridSize === 'compact' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                        title="Tarjetas compactas"
                      >
                        <MdApps className={`text-xl ${gridSize === 'compact' ? 'text-blue-600' : 'text-gray-500'}`} />
                      </button>
                    </div>
                  )}
                  {/* Toggle de vista */}
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      title="Vista de cuadrícula"
                    >
                      <MdViewModule className={`text-xl ${viewMode === 'grid' ? 'text-blue-600' : 'text-gray-500'}`} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-all ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      title="Vista de lista"
                    >
                      <MdViewList className={`text-xl ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-500'}`} />
                    </button>
                  </div>
                  {data && (
                    <span className="text-sm text-gray-600">
                      {esTabPacks
                        ? `${content.length} pack${content.length === 1 ? '' : 's'} en esta página`
                        : searchTerm
                        // Con búsqueda activa no conocemos el total de resultados (por eso
                        // se sacó el COUNT) — mostrar el total del catálogo acá sería
                        // engañoso, así que solo se informa lo que trajo esta página.
                        ? `${content.length} producto${content.length === 1 ? '' : 's'} en esta página`
                        : countData
                          ? `${content.length} de ${countData.total} productos`
                          : `${content.length} producto${content.length === 1 ? '' : 's'}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Área scrollable de productos */}
              <div className="flex-1 overflow-y-auto px-4 py-3">

              {/* Estados de carga y error */}
              {isLoading && (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
                  <p className="mt-4 text-gray-600">Cargando productos...</p>
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-4">Error al cargar productos</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: '#43BFFF' }}
                  >
                    <MdRefresh className="inline mr-2" />
                    Reintentar
                  </button>
                </div>
              )}

              {/* Grid de productos */}
              {!isLoading && !error && data && (
                <>
                  {content.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <MdSearch className="mx-auto text-6xl mb-4 text-gray-300" />
                      <p>{esTabPacks && !searchTerm ? 'No tienes packs creados' : esTabPacks ? 'No se encontraron packs' : 'No se encontraron productos'}</p>
                      {esTabPacks && !searchTerm && (
                        <button
                          onClick={() => navigate('/inventario/crear-pack')}
                          className="mt-3 text-sm font-semibold underline text-purple-700 hover:text-purple-900"
                        >
                          Crear un pack
                        </button>
                      )}
                      {searchTerm && (
                        <p className="text-sm mt-2">
                          Intenta con otra búsqueda
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Vista Grid */}
                      {viewMode === 'grid' && (
                        <div className={gridSize === 'compact'
                          ? 'grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-1.5'
                          : 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2'
                        }>
                          {content.map((producto) => (
                            <div
                              key={producto.productoAlmacenId}
                              className={`border-2 border-gray-200 rounded-xl hover:shadow-lg transition-all cursor-pointer hover:border-blue-300 ${gridSize === 'compact' ? 'p-1.5' : 'p-2'}`}
                              onClick={() => handleAddToCart(producto)}
                            >
                              {/* Imagen */}
                              <div className="rounded-lg overflow-hidden bg-gray-100 relative group mb-1.5 aspect-square">
                                <img
                                  src={producto.imagenUrl || productosPlaceholder}
                                  alt={producto.productoNombre}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = productosPlaceholder;
                                  }}
                                />
                                <button
                                  onClick={(e) => handleClickImagen(e, producto)}
                                  className="absolute top-1 right-1 p-1 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Cambiar imagen"
                                >
                                  <MdImage className="text-sm" style={{ color: '#43BFFF' }} />
                                </button>
                              </div>

                              {/* Info */}
                              <h3 className="font-bold text-xs mb-1 truncate" style={{ color: '#11194F' }} title={producto.productoNombre}>
                                {producto.productoNombre}
                              </h3>

                              {gridSize === 'normal' && producto.codigo && (
                                <p className="text-[10px] text-gray-500 mb-1 truncate">
                                  Cód: {producto.codigo}
                                </p>
                              )}

                              {/* Precio y Stock */}
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-xs" style={{ color: '#10B981' }}>
                                  ${producto.precioVentaActual?.toLocaleString('es-CL') || 'N/A'}
                                </span>

                                {gridSize === 'normal' && (
                                  producto.esPack ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                      Pack
                                    </span>
                                  ) : (
                                    <span
                                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                                        producto.stock > 10
                                          ? 'bg-green-100 text-green-700'
                                          : producto.stock > 0
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-red-100 text-red-700'
                                      }`}
                                    >
                                      {producto.stock <= 0
                                        ? 'Sin stock'
                                        : producto.stock <= 10
                                        ? `Quedan ${producto.venderPor === 'F' ? producto.stock.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : producto.stock}`
                                        : (producto.venderPor === 'F' ? producto.stock.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : producto.stock)}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Vista Lista */}
                      {viewMode === 'list' && (
                        <div className="space-y-2">
                        {content.map((producto) => (
                          <div
                            key={producto.productoAlmacenId}
                            className="border-2 border-gray-200 rounded-lg p-3 hover:shadow-md transition-all cursor-pointer hover:border-blue-300 flex items-center gap-4"
                            onClick={() => handleAddToCart(producto)}
                          >
                            {/* Imagen pequeña */}
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 relative group">
                              <img
                                src={producto.imagenUrl || productosPlaceholder}
                                alt={producto.productoNombre}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = productosPlaceholder;
                                }}
                              />
                              {/* Botón para cambiar imagen */}
                              <button
                                onClick={(e) => handleClickImagen(e, producto)}
                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-40 opacity-0 group-hover:opacity-100 transition-all"
                                title="Cambiar imagen"
                              >
                                <MdImage className="text-2xl text-white" />
                              </button>
                            </div>

                            {/* Información del producto */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-sm mb-1" style={{ color: '#11194F' }}>
                                {producto.productoNombre}
                              </h3>
                              {producto.codigo && (
                                <p className="text-xs text-gray-500">
                                  Código: {producto.codigo}
                                </p>
                              )}
                            </div>

                            {/* Stock/Badge */}
                            <div className="flex-shrink-0">
                              {producto.esPack ? (
                                <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 font-medium">
                                  Pack
                                </span>
                              ) : (
                                <span
                                  className={`text-xs px-2 py-1 rounded font-medium ${
                                    producto.stock > 10
                                      ? 'bg-green-100 text-green-700'
                                      : producto.stock > 0
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {producto.stock <= 0
                                    ? 'Sin stock'
                                    : producto.stock <= 10
                                    ? `Quedan ${producto.venderPor === 'F' ? producto.stock.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : producto.stock}`
                                    : (producto.venderPor === 'F' ? producto.stock.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : producto.stock)}
                                </span>
                              )}
                            </div>

                            {/* Precio */}
                            <div className="flex-shrink-0 text-right">
                              <span className="font-bold text-lg" style={{ color: '#10B981' }}>
                                ${producto.precioVentaActual?.toLocaleString('es-CL') || 'N/A'}
                              </span>
                            </div>
                          </div>
                        ))}
                        </div>
                      )}

                      {/* Anterior / Siguiente */}
                      {(currentPage > 0 || hasNextPage) && (
                        <div className="flex items-center justify-center gap-4 mt-6">
                          <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                            className="px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              backgroundColor: currentPage === 0 ? '#E5E7EB' : '#43BFFF',
                              color: currentPage === 0 ? '#9CA3AF' : '#FFFFFF',
                            }}
                          >
                            Anterior
                          </button>

                          <span className="text-sm text-gray-600">
                            Página {currentPage + 1}
                          </span>

                          <button
                            onClick={handleNextPage}
                            disabled={!hasNextPage}
                            className="px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              backgroundColor: !hasNextPage ? '#E5E7EB' : '#43BFFF',
                              color: !hasNextPage ? '#9CA3AF' : '#FFFFFF',
                            }}
                          >
                            Siguiente
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              </div>{/* fin área scrollable productos */}
            </div>{/* fin panel productos */}
          </div>{/* fin col-span-7 */}

          {/* Panel Derecho - Carrito (solo visible en desktop) */}
          <div className="hidden lg:flex lg:col-span-5 flex-col h-full overflow-hidden">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden">
              {/* Header del carrito */}
              <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MdShoppingCart className="text-xl" style={{ color: '#43BFFF' }} />
                    <h2 className="text-lg font-bold" style={{ color: '#11194F' }}>
                      Carrito
                    </h2>
                  </div>

                  {cartItems.length > 0 && (
                    <button
                      onClick={() => setShowConfirmClearCart(true)}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              {/* Items del carrito */}
              <div className="flex-1 overflow-y-auto px-3 py-2">
                {cartItems.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <MdShoppingCart className="mx-auto text-6xl mb-3" />
                    <p>Carrito vacío</p>
                    <p className="text-sm mt-1">Agrega productos para comenzar</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {[...cartItems].reverse().map((item, reversedIndex) => {
                    const index = cartItems.length - 1 - reversedIndex;
                    return (
                      <div
                        key={`${item.producto.productoAlmacenId}-${index}`}
                        className={`p-2 rounded-lg border-2 transition-all ${
                          selectedItemIndex === index
                            ? 'border-blue-400 bg-blue-50'
                            : item.producto.productoAlmacenId === lastInteractedId
                            ? 'border-emerald-300 bg-emerald-50 hover:border-emerald-400'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => selectItem(index)}
                      >
                        {/* Fila superior: nombre + precio + menú ⋮ + eliminar */}
                        <div className="flex items-center gap-1 mb-1.5">
                          <h4
                            className="font-semibold text-sm truncate flex-1 min-w-0"
                            style={{ color: '#11194F' }}
                            title={item.packDetalle ? item.packDetalle.nombrePack : item.producto.productoNombre}
                          >
                            {item.packDetalle ? item.packDetalle.nombrePack : item.producto.productoNombre}
                          </h4>
                          <span className="font-semibold text-base flex-shrink-0" style={{ color: '#11194F' }}>
                            ${item.subtotal.toLocaleString('es-CL')}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeItem(item.producto.productoAlmacenId);
                              addToast('info', 'Producto eliminado');
                            }}
                            className="w-7 h-7 flex items-center justify-center text-red-500 hover:text-red-700 flex-shrink-0"
                          >
                            <MdDelete className="text-lg" />
                          </button>
                        </div>

                        {/* Descuentos y detalles pack */}
                        {item.packDetalle && item.descuento != null && item.descuento > 0 && (
                          <p className="text-xs text-green-600 font-medium mb-1">
                            Ahorro: ${item.descuento.toLocaleString('es-CL')}
                          </p>
                        )}
                        {item.packDetalle && item.descuento != null && item.descuento < 0 && (
                          <p className="text-xs text-orange-600 font-medium mb-1">
                            Recargo: ${Math.abs(item.descuento).toLocaleString('es-CL')} (este pack cuesta más que sus productos por separado)
                          </p>
                        )}
                        {!item.packDetalle && item.descuento && item.descuento > 0 && (
                          <p className="text-xs text-orange-600 font-medium mb-1">
                            Descuento: -${item.descuento.toLocaleString('es-CL')}
                          </p>
                        )}
                        {item.packDetalle && (
                          <div className="mb-1.5 pl-2 border-l-2 border-blue-300">
                            {item.packDetalle.products.map((prod, idx) => (
                              <div key={idx} className="text-xs text-gray-600 py-0.5">
                                {prod.quantity} - {prod.nombre}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Controles de cantidad */}
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDecrement(
                                item.producto.productoAlmacenId,
                                item.cantidad,
                                item.producto.venderPor,
                                !!item.packDetalle
                              );
                            }}
                            className="w-8 h-8 rounded flex items-center justify-center text-white transition-all hover:scale-110 flex-shrink-0"
                            style={{ backgroundColor: '#EF4444' }}
                          >
                            <MdRemove />
                          </button>

                          <input
                            type="text"
                            inputMode={item.packDetalle || item.producto.venderPor === 'U' ? 'numeric' : 'decimal'}
                            value={editingQuantity[item.producto.productoAlmacenId] ?? String(item.cantidad).replace('.', ',')}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(
                                item.producto.productoAlmacenId,
                                e.target.value,
                                item.producto.venderPor,
                                !!item.packDetalle
                              );
                            }}
                            onBlur={(e) => {
                              e.stopPropagation();
                              handleQuantityBlur(item.producto.productoAlmacenId);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                          />

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleIncrement(
                                item.producto.productoAlmacenId,
                                item.cantidad,
                                item.producto.venderPor,
                                !!item.packDetalle
                              );
                            }}
                            className="w-8 h-8 rounded flex items-center justify-center text-white transition-all hover:scale-110 flex-shrink-0"
                            style={{ backgroundColor: '#10B981' }}
                          >
                            <MdAdd />
                          </button>

                          {/* Botón descuento (solo productos normales) */}
                          {!item.packDetalle && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDiscountModal(item);
                              }}
                              className="w-8 h-8 rounded flex items-center justify-center text-white transition-all hover:scale-110 flex-shrink-0"
                              style={{ backgroundColor: '#F59E0B' }}
                              title="Aplicar descuento"
                            >
                              <span className="text-xs font-bold">%</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );})}
                  </div>
                )}
              </div>

              {/* Footer - Total */}
              <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0">
                {/* Resumen */}
                <div className="space-y-1 mb-3">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Productos ({itemCount})</span>
                  </div>
                  {cartItems.some(i => i.descuento && i.descuento > 0) && (
                    <div className="flex justify-between text-xs text-orange-600">
                      <span>Descuentos:</span>
                      <span>-${cartItems.reduce((s, i) => s + (i.descuento || 0), 0).toLocaleString('es-CL')}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold" style={{ fontSize: '26px', color: '#11194F' }}>
                    <span>Total:</span>
                    <span>${total.toLocaleString('es-CL')}</span>
                  </div>
                </div>

                {/* Botón Finalizar Venta */}
                <button
                  disabled={cartItems.length === 0}
                  className="w-full rounded-xl font-bold text-lg text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ backgroundColor: '#10B981', height: '56px' }}
                  onClick={() => setShowCheckoutModal(true)}
                >
                  Pagar Venta
                </button>
              </div>
            </div>
          </div>{/* fin col-span-5 carrito */}
        </div>{/* fin grid lg:grid-cols-12 */}
      </div>{/* fin contenido principal flex-1 */}

      {/* Footer con botones fijos (solo móvil y tablet) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white shadow-2xl z-40">
        <div className="flex items-end justify-around px-4 py-1 relative">
          {/* Botón Calculadora */}
          <button
            onClick={() => setShowCalculator(true)}
            className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            title="Calculadora"
          >
            <FaCalculator className="text-2xl text-gray-500" />
            <span className="text-xs font-medium text-gray-500">Calculadora</span>
          </button>

          {/* Botón Caja */}
          <button
            onClick={handleIrAMiCaja}
            className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            title="Gestionar caja"
          >
            <MdPointOfSale className="text-2xl text-gray-500" />
            <span className="text-xs font-medium text-gray-500">Caja</span>
          </button>

          {/* Botón Carrito (destacado y elevado) */}
          <button
            onClick={() => setShowMobileCart(true)}
            className="flex flex-col items-center justify-center rounded-full shadow-2xl transition-all hover:scale-110 active:scale-95 relative"
            style={{ 
              backgroundColor: '#10B981',
              width: '70px',
              height: '70px',
              transform: 'translateY(-20px)',
              border: '4px solid white'
            }}
            title="Ver carrito"
          >
            <MdShoppingCart className="text-3xl text-white" />
            {itemCount > 0 && (
              <>
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center border-2 border-white">
                  {itemCount}
                </span>
              </>
            )}
          </button>

          {/* Botón Imprimir */}
          <button
            onClick={() => ultimaBoletaId && printBoleta(ultimaBoletaId)}
            disabled={ultimaBoletaId === null || loadingPrintId !== null}
            className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={ultimaBoletaId ? 'Reimprimir última venta' : 'No hay venta reciente'}
          >
            {loadingPrintId === ultimaBoletaId && ultimaBoletaId !== null ? (
              <MdRefresh className="text-2xl animate-spin text-gray-500" />
            ) : (
              <MdPrint className="text-2xl text-gray-500" />
            )}
            <span className="text-xs font-medium text-gray-500">Imprimir</span>
          </button>

          {/* Botón Cámara */}
          <button
            onClick={() => setShowBarcodeScanner(true)}
            className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            title="Escanear con cámara"
          >
            <CiBarcode className="text-2xl text-gray-600" />
            <span className="text-xs font-medium text-gray-600">Cámara</span>
          </button>
        </div>
      </div>

      {/* Sidebar del carrito móvil */}
      {showMobileCart && (
        <>
          {/* Overlay */}
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 animate-fadeIn"
            onClick={() => setShowMobileCart(false)}
          />
          
          {/* Sidebar */}
          <div className="lg:hidden fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col animate-slideInRight">
            {/* Header del carrito móvil */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MdShoppingCart className="text-2xl" style={{ color: '#43BFFF' }} />
                <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
                  Carrito
                </h2>
              </div>
              
              <div className="flex items-center gap-3">
                {cartItems.length > 0 && (
                  <button
                    onClick={() => setShowConfirmClearCart(true)}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Limpiar
                  </button>
                )}
                <button
                  onClick={() => setShowMobileCart(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <MdClose className="text-2xl text-gray-600" />
                </button>
              </div>
            </div>

            {/* Items del carrito móvil */}
            <div className="flex-1 px-6 py-4 overflow-y-auto">
              {cartItems.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MdShoppingCart className="mx-auto text-6xl mb-3" />
                  <p>Carrito vacío</p>
                  <p className="text-sm mt-1">Agrega productos para comenzar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cartItems.map((item, index) => (
                    <div
                      key={`${item.producto.productoAlmacenId}-${index}`}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedItemIndex === index
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => selectItem(index)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 mr-2">
                          <h4 className="font-semibold text-sm" style={{ color: '#11194F' }}>
                            {item.packDetalle ? item.packDetalle.nombrePack : item.producto.productoNombre}
                          </h4>
                          {item.packDetalle && item.descuento != null && item.descuento > 0 && (
                            <p className="text-xs text-green-600 font-medium mt-1">
                              Ahorro: ${item.descuento.toLocaleString('es-CL')}
                            </p>
                          )}
                          {item.packDetalle && item.descuento != null && item.descuento < 0 && (
                            <p className="text-xs text-orange-600 font-medium mt-1">
                              Recargo: ${Math.abs(item.descuento).toLocaleString('es-CL')}
                            </p>
                          )}
                          {!item.packDetalle && item.descuento && item.descuento > 0 && (
                            <p className="text-xs text-orange-600 font-medium mt-1">
                              Descuento: -${item.descuento.toLocaleString('es-CL')}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem(item.producto.productoAlmacenId);
                            addToast('info', 'Producto eliminado');
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>

                      {item.packDetalle && (
                        <div className="mb-2 pl-2 border-l-2 border-blue-300">
                          {item.packDetalle.products.map((prod, idx) => (
                            <div key={idx} className="text-xs text-gray-600 py-1">
                              {prod.quantity} - {prod.nombre}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDecrement(
                              item.producto.productoAlmacenId,
                              item.cantidad,
                              item.producto.venderPor,
                              !!item.packDetalle
                            );
                          }}
                          className="w-7 h-7 rounded flex items-center justify-center text-white transition-all hover:scale-110"
                          style={{ backgroundColor: '#EF4444' }}
                        >
                          <MdRemove />
                        </button>

                        <input
                          type="text"
                          inputMode={item.packDetalle || item.producto.venderPor === 'U' ? 'numeric' : 'decimal'}
                          value={editingQuantity[item.producto.productoAlmacenId] ?? String(item.cantidad).replace('.', ',')}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleQuantityChange(
                              item.producto.productoAlmacenId,
                              e.target.value,
                              item.producto.venderPor,
                              !!item.packDetalle
                            );
                          }}
                          onBlur={(e) => {
                            e.stopPropagation();
                            handleQuantityBlur(item.producto.productoAlmacenId);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                        />

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIncrement(
                              item.producto.productoAlmacenId,
                              item.cantidad,
                              item.producto.venderPor,
                              !!item.packDetalle
                            );
                          }}
                          className="w-7 h-7 rounded flex items-center justify-center text-white transition-all hover:scale-110"
                          style={{ backgroundColor: '#10B981' }}
                        >
                          <MdAdd />
                        </button>

                        {!item.packDetalle && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDiscountModal(item);
                            }}
                            className="w-7 h-7 rounded flex items-center justify-center text-white transition-all hover:scale-110"
                            style={{ backgroundColor: '#F59E0B' }}
                            title="Aplicar descuento"
                          >
                            <span className="text-xs font-bold">%</span>
                          </button>
                        )}

                        <span className="text-xs text-gray-500 ml-auto">
                          ${item.subtotal.toLocaleString('es-CL')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - Total móvil */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white" style={{ paddingBottom: '100px' }}>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Productos ({itemCount})</span>
                </div>
                <div className="flex justify-between text-2xl font-bold" style={{ color: '#11194F' }}>
                  <span>Total:</span>
                  <span>${total.toLocaleString('es-CL')}</span>
                </div>
              </div>

              <button
                disabled={cartItems.length === 0}
                className="w-full py-4 rounded-xl font-bold text-lg text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ backgroundColor: '#10B981' }}
                onClick={() => {
                  setShowMobileCart(false);
                  setShowCheckoutModal(true);
                }}
              >
                Pagar Venta
              </button>
            </div>
          </div>
        </>
      )}

      {/* Calculator Modal */}
      <CalculatorModal
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
        onApply={handleApplyCalculator}
      />

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onScan={async (barcode) => {
          setShowBarcodeScanner(false);
          await findAndAddForBarcode(barcode);
        }}
        title="Escanear Código de Barras"
      />

      {/* Quantity Modal for Fractional Products */}
      {showQuantityModal && selectedProductForQuantity && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
                Ingresa la cantidad
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedProductForQuantity.productoNombre}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              {/* Toggle modo ingreso */}
              <div className="flex rounded-xl overflow-hidden border border-gray-300 mb-4">
                <button
                  onClick={() => { setGranelInputMode('kg'); setQuantityInput(''); }}
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${granelInputMode === 'kg' ? 'text-white' : 'text-gray-600 bg-white hover:bg-gray-50'}`}
                  style={granelInputMode === 'kg' ? { backgroundColor: '#11194F' } : {}}
                >
                  Ingresar kg
                </button>
                <button
                  onClick={() => { setGranelInputMode('monto'); setQuantityInput(''); }}
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${granelInputMode === 'monto' ? 'text-white' : 'text-gray-600 bg-white hover:bg-gray-50'}`}
                  style={granelInputMode === 'monto' ? { backgroundColor: '#11194F' } : {}}
                >
                  Ingresar monto ($)
                </button>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                {granelInputMode === 'kg' ? 'Cantidad (kg)' : 'Monto a vender ($)'}
              </label>
              <input
                ref={quantityInputRef}
                type="text"
                value={quantityInput}
                onChange={(e) => {
                  const val = e.target.value;
                  if (granelInputMode === 'monto') {
                    // Solo dígitos enteros en modo monto, máximo 9 dígitos ($999.999.999)
                    if (/^\d*$/.test(val) && val.length <= 9) setQuantityInput(val);
                  } else {
                    setQuantityInput(val);
                  }
                }}
                inputMode={granelInputMode === 'monto' ? 'numeric' : 'decimal'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmQuantity();
                  }
                }}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder={granelInputMode === 'kg' ? 'Ej: 1,5' : 'Ej: 1500'}
              />

              {/* Info de precio */}
              <div className="mt-4 p-3 rounded-lg bg-gray-50">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Precio por kg:</span>
                  <span className="font-semibold" style={{ color: '#11194F' }}>
                    {selectedProductForQuantity.precioVentaActual != null
                      ? `$${selectedProductForQuantity.precioVentaActual.toLocaleString('es-CL')}`
                      : 'Sin precio'}
                  </span>
                </div>
                {quantityInput && (() => {
                  const rawNormalized = granelInputMode === 'monto'
                    ? quantityInput
                    : quantityInput.replace(',', '.');
                  const valor = parseFloat(rawNormalized);
                  if (isNaN(valor) || valor <= 0) return null;
                  const precio = selectedProductForQuantity.precioVentaActual || 0;
                  const subtotal = granelInputMode === 'kg'
                    ? Math.round(precio * valor)
                    : Math.round(valor);
                  const kgs = granelInputMode === 'monto' && precio > 0
                    ? Math.round((valor / precio) * 1000) / 1000
                    : null;
                  return (
                    <>
                      {kgs !== null && (
                        <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                          <span className="text-gray-600">Peso equivalente:</span>
                          <span className="font-semibold" style={{ color: '#11194F' }}>{kgs} kg</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-bold text-lg" style={{ color: '#10B981' }}>
                          ${subtotal.toLocaleString('es-CL')}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowQuantityModal(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmQuantity}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 active:scale-95"
                style={{ backgroundColor: '#10B981' }}
              >
                Agregar al Carrito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pack Modal */}
      {showPackModal && selectedPackId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
                Detalles del Pack
              </h2>
              {packData && (
                <p className="text-sm text-gray-600 mt-1">
                  {packData.nombrePack}
                </p>
              )}
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              {isLoadingPack ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-500"></div>
                  <p className="mt-3 text-gray-600">Cargando pack...</p>
                </div>
              ) : packData ? (
                <>
                  {/* Lista de productos en el pack */}
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      Productos incluidos:
                    </h3>
                    <div className="space-y-2">
                      {packData.products.map((prod, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {prod.quantity}x
                            </span>
                            <span className="text-sm text-gray-700">{prod.nombre}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            ${prod.total.toLocaleString('es-CL')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Resumen de precios */}
                  <div className="border-t border-gray-200 pt-4 space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Precio normal:</span>
                      <span className="line-through">
                        ${packData.precioRealTotal.toLocaleString('es-CL')}
                      </span>
                    </div>
                    {packData.ahorroTotal > 0 ? (
                      <div className="flex justify-between text-sm font-semibold text-green-600">
                        <span>Ahorro:</span>
                        <span>-${packData.ahorroTotal.toLocaleString('es-CL')}</span>
                      </div>
                    ) : packData.ahorroTotal < 0 ? (
                      <div className="flex justify-between text-sm font-semibold text-orange-600">
                        <span>Recargo:</span>
                        <span>+${Math.abs(packData.ahorroTotal).toLocaleString('es-CL')}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between text-xl font-bold" style={{ color: '#11194F' }}>
                      <span>Precio Pack:</span>
                      <span>${packData.precioPackTotal.toLocaleString('es-CL')}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No se pudo cargar la información del pack
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowPackModal(false);
                  setSelectedPackId(null);
                  setSelectedPackProducto(null);
                }}
                className="flex-1 py-3 rounded-xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmPack}
                disabled={!packData}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#10B981' }}
              >
                Agregar al Carrito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && selectedProductForDiscount && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
                Aplicar Descuento
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedProductForDiscount.nombre}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              {/* Selector de tipo de descuento */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de descuento
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setDiscountType('porcentaje');
                      setDiscountInput('');
                    }}
                    className={`py-3 rounded-xl font-semibold transition-all ${
                      discountType === 'porcentaje'
                        ? 'text-white'
                        : 'text-gray-700 bg-gray-200 hover:bg-gray-300'
                    }`}
                    style={{
                      backgroundColor: discountType === 'porcentaje' ? '#F59E0B' : undefined
                    }}
                  >
                    Porcentaje (%)
                  </button>
                  <button
                    onClick={() => {
                      setDiscountType('monto');
                      setDiscountInput('');
                    }}
                    className={`py-3 rounded-xl font-semibold transition-all ${
                      discountType === 'monto'
                        ? 'text-white'
                        : 'text-gray-700 bg-gray-200 hover:bg-gray-300'
                    }`}
                    style={{
                      backgroundColor: discountType === 'monto' ? '#F59E0B' : undefined
                    }}
                  >
                    Monto ($)
                  </button>
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                {discountType === 'porcentaje' ? 'Descuento en porcentaje' : 'Descuento en pesos'}
              </label>
              <input
                type="number"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmDiscount();
                  }
                }}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder={discountType === 'porcentaje' ? 'Ej: 10' : 'Ej: 500'}
                min="0"
                max={discountType === 'porcentaje' ? '100' : undefined}
                step={discountType === 'porcentaje' ? 'any' : '1'}
              />
              
              {/* Info de precios */}
              <div className="mt-4 p-3 rounded-lg bg-gray-50">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal sin descuento:</span>
                  <span className="font-semibold" style={{ color: '#11194F' }}>
                    ${(selectedProductForDiscount.precio * selectedProductForDiscount.cantidad).toLocaleString('es-CL')}
                  </span>
                </div>
                {discountInput && parseFloat(discountInput) >= 0 && (() => {
                  const subtotal = selectedProductForDiscount.precio * selectedProductForDiscount.cantidad;
                  let descuentoEnPesos = 0;
                  
                  if (discountType === 'porcentaje') {
                    descuentoEnPesos = descuentoPorcentualEnPesos(subtotal, parseFloat(discountInput));
                  } else {
                    descuentoEnPesos = parseFloat(discountInput);
                  }
                  
                  return (
                    <>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-gray-600">Descuento:</span>
                        <span className="text-orange-600 font-medium">
                          -{discountType === 'porcentaje' && `${discountInput}% `}${descuentoEnPesos.toLocaleString('es-CL')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-bold text-lg" style={{ color: '#10B981' }}>
                          ${Math.max(1, subtotal - descuentoEnPesos).toLocaleString('es-CL')}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <p className="text-xs text-gray-500 mt-3">
                {discountType === 'porcentaje' 
                  ? 'Tip: El porcentaje debe ser entre 0 y 100. Ingresa 0 para eliminar el descuento.'
                  : 'Tip: El descuento no puede dejar el producto en $0. Ingresa 0 para eliminar el descuento.'
                }
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setSelectedProductForDiscount(null);
                  setDiscountInput('');
                  setDiscountType('porcentaje');
                }}
                className="flex-1 py-3 rounded-xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDiscount}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 active:scale-95"
                style={{ backgroundColor: '#F59E0B' }}
              >
                Aplicar Descuento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <ModalCheckout
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        onSaleCompleted={handleSaleCompleted}
      />

      {/* Modal de búsqueda de imágenes */}
      <BuscarImagenModal
        isOpen={mostrarModalImagen}
        onClose={() => {
          setMostrarModalImagen(false);
          setProductoSeleccionadoParaImagen(null);
        }}
        onSelectImage={handleSeleccionarImagen}
        initialSearch={productoSeleccionadoParaImagen?.productoNombre || ''}
        productName={productoSeleccionadoParaImagen?.productoNombre}
      />

      {/* Modal de Agregar Producto Global al Inventario */}
      {showAddProductModal && productoGlobalEncontrado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
                Agregar el Producto al Inventario
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Este producto existe en la base de datos pero no en tu inventario
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              {/* Código de Barras */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código de Barras
                </label>
                <div className="flex items-center gap-2">
                  <FaBarcode className="text-2xl text-gray-400" />
                  <input
                    type="text"
                    value={productoGlobalEncontrado.codigo || 'Sin código'}
                    disabled
                    className="flex-1 px-4 py-3 text-lg border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Nombre del Producto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Producto
                </label>
                <input
                  type="text"
                  value={productoGlobalEncontrado.nombre}
                  disabled
                  className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Precio de Venta */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Precio de Venta *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-lg font-semibold text-gray-600">
                    $
                  </span>
                  <input
                    type="text"
                    value={precioVentaInput ? parseInt(precioVentaInput).toLocaleString('es-CL') : ''}
                    onChange={handlePrecioVentaChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleConfirmAddProduct();
                      }
                    }}
                    className="w-full pl-8 pr-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    placeholder="0"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Precio al que venderás este producto
                </p>
              </div>

              {/* Vista previa del valor */}
              {precioVentaInput && parseInt(precioVentaInput) > 0 && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Precio de Venta:</span>
                    <span className="text-lg font-bold" style={{ color: '#11194F' }}>
                      ${parseInt(precioVentaInput).toLocaleString('es-CL')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowAddProductModal(false);
                  setProductoGlobalEncontrado(null);
                  setPrecioVentaInput('');
                }}
                disabled={agregarProductoMutation.isPending}
                className="flex-1 py-3 rounded-xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmAddProduct}
                disabled={!precioVentaInput || parseInt(precioVentaInput) <= 0 || agregarProductoMutation.isPending}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: '#10B981' }}
              >
                {agregarProductoMutation.isPending ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Procesando...</span>
                  </>
                ) : (
                  'Agregar al Inventario'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aviso: no hay una caja abierta hoy (Caja 360) */}
      {showAvisoCajaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-2" style={{ color: '#11194F' }}>
              No tienes una caja abierta hoy
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Ábrela para llevar el registro de tus ventas del día.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAvisoCajaModal(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Ahora no
              </button>
              <button
                onClick={() => {
                  setShowAvisoCajaModal(false);
                  setShowGestionCajaModal(true);
                }}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 active:scale-95"
                style={{ backgroundColor: '#10B981' }}
              >
                Abrir mi caja
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gestión de caja: abrir/cerrar turno sin salir de Ventas (Caja 360) */}
      {showGestionCajaModal && (
        <GestionCajaModal onClose={() => setShowGestionCajaModal(false)} />
      )}

      {/* Modal de Confirmación para Limpiar Carrito */}
      <ConfirmacionModal
        isOpen={showConfirmClearCart}
        onClose={() => setShowConfirmClearCart(false)}
        onConfirm={handleConfirmClearCart}
        title="Limpiar Carrito"
        message="¿Estás seguro de que quieres vaciar todo el carrito? Esta acción no se puede deshacer."
        confirmText="Limpiar Carrito"
        cancelText="Cancelar"
        variant="warning"
      />

      {/* Indicador flotante de escaneo detectado */}
      {scanDetected && (
        <div className="fixed bottom-24 lg:bottom-8 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
          <div className="p-4 rounded-xl bg-green-50 shadow-2xl flex items-center gap-3 animate-slideInUp border-2" style={{ maxWidth: '400px', borderColor: '#10B981' }}>
            <FaBarcode className="text-3xl text-green-600" />
            <div>
              <p className="text-base font-bold text-gray-700">
                ✓ Código escaneado
              </p>
              <p className="text-sm text-gray-400">
                Buscando producto...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

